import { useContext } from 'react';
import { FormattedNumber } from '@sm/intl';
import { StaticContext } from '@sm/webassets';

import { GUAC_COUNTRY_CODES } from './constants';

/**
 * Creates a className friendly string from a package label
 *
 * @param {Object} packageObj The package object to create the class name from
 * @return The label name, ready to be used as a className
 */
export function packageLabelToClassName(packageObj) {
  return packageObj.label.trim().replace(/\s/g, '-').toLowerCase();
}

export function monthlyCostInDollars(skuCost) {
  return parseInt(skuCost.cost, 10) / 100 / 12;
}

export function formatMonthlyCostInLocaleCurrency(skuCost) {
  const monthlyCost = monthlyCostInDollars(skuCost);
  return FormattedNumber({
    value: monthlyCost,
    formatStyle: 'currency',
    currency: skuCost.currency.code,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
  });
}

export function formatCostInLocaleCurrency(skuCost) {
  return FormattedNumber({
    value: parseInt(skuCost.cost, 10) / 100,
    formatStyle: 'currency',
    currency: skuCost.currency.code,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
  });
}

export function getCoreCostFromSkus(skuCosts) {
  return skuCosts.find(sku => sku.skuType === 'SUBSCRIPTION_CORE_SEAT');
}

/**
 * Given two skuCosts, returns the rounded percentage savings between them
 *
 * @param {object} skuCostOriginal The originally priced skuCost object
 * @param {object} skuCostDiscounted The discounted priced skuCost object
 */
export function calculateSavingsPercent(skuCostOriginal, skuCostDiscounted) {
  let retVal = 0;
  const costDifference = skuCostOriginal.cost - skuCostDiscounted.cost;

  // If the "discounted" price is greater, we'll just ignore that...
  if (costDifference > 0) {
    retVal = Math.round((costDifference / skuCostOriginal.cost) * 100);
  }

  return retVal;
}

// The following helpers are meant to only be used in a feature's `hidden` getter.
// The hooks lint check will be disabled because they will only be invoked during
// a component's render cycle. These methods should NEVER be invoked anywhere else!

/* eslint-disable react-hooks/rules-of-hooks */

/**
 * Used as part of a feature's `hidden` property to limit its
 * display to only GB, US, AU, and CA.
 *
 * NOTE: Uses react hooks, so can only be called from within a
 *       react component. Do not use outside of a package feature's
 *       `hidden` getter.
 *
 * @return {boolean}
 */
export function featureIsGuacOnly() {
  const {
    environment: { countryCode },
  } = useContext(StaticContext);
  return !GUAC_COUNTRY_CODES.includes(countryCode.toLowerCase());
}

/**
 * Used as part of a feature's `hidden` property to limit its
 * display to non GB, US, AU, or CA countries
 *
 * NOTE: Uses react hooks, so can only be called from within a
 *       react component. Do not use outside of a package feature's
 *       `hidden` getter.
 *
 * @return {boolean}
 */
export function featureIsNonGuacOnly() {
  return !featureIsGuacOnly();
}

/* eslint-enable react-hooks/rules-of-hooks */
